import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { hiringReferralsApiService } from '../../services/apiServices';
import {
  ReferralApplicationResponse,
  ReferralApplicationStatus,
  ResumeType,
} from '../../api-client';
import { getReferralApplicationStatusLabel, getResumeTypeLabel } from '../utils/statusConverters';

const HiringReferralsDetailPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<ReferralApplicationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationId) {
      navigate('/hiring/referrals');
      return;
    }

    const loadApplication = async () => {
      try {
        setLoading(true);
        const response = await hiringReferralsApiService.getApplication(applicationId);
        setApplication(response.data || null);
      } catch (err: any) {
        console.error('Ошибка при загрузке заявки:', err);
        setError(err.message || 'Ошибка при загрузке заявки');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const handleStatusUpdate = async (newStatus: ReferralApplicationStatus) => {
    if (!applicationId) return;

    try {
      setUpdating(true);
      setUpdateError(null);
      await hiringReferralsApiService.updateStatus(applicationId, newStatus);

      // Обновляем локальное состояние
      setApplication((prev) => (prev ? { ...prev, status: newStatus } : null));
    } catch (err: any) {
      console.error('Ошибка при обновлении статуса:', err);
      setUpdateError(err.message || 'Ошибка при обновлении статуса');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadResume = async () => {
    if (!application || !applicationId) return;

    try {
      setDownloadingFile(true);
      setFileError(null);

      const response = await hiringReferralsApiService.getApplicationFileUrl(applicationId);
      const fileUrl = response.data?.resume_file_url;

      if (!fileUrl) {
        throw new Error('Не удалось получить ссылку на файл');
      }

      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('Ошибка при скачивании резюме:', err);
      setFileError(err.message || 'Ошибка при скачивании резюме');
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleOpenResumeUrl = () => {
    if (!application?.resume_url) return;
    window.open(application.resume_url, '_blank', 'noopener,noreferrer');
  };

  const getStatusBadgeVariant = (status: ReferralApplicationStatus) => {
    switch (status) {
      case ReferralApplicationStatus.PENDING:
        return 'warning';
      case ReferralApplicationStatus.REVIEWING:
        return 'info';
      case ReferralApplicationStatus.ACCEPTED:
        return 'success';
      case ReferralApplicationStatus.REJECTED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Загрузка заявки...</p>
        </div>
      </Container>
    );
  }

  if (error || !application) {
    return (
      <Container fluid>
        <Row className="mt-4">
          <Col>
            <Alert variant="danger">{error || 'Заявка не найдена'}</Alert>
            <Button variant="primary" onClick={() => navigate('/hiring/referrals')}>
              Вернуться к списку
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявка №{application.id}</h2>
            <Link to="/hiring/referrals">
              <Button variant="outline-secondary">Вернуться к списку</Button>
            </Link>
          </div>

          {updateError && <Alert variant="danger">{updateError}</Alert>}
          {fileError && <Alert variant="warning">{fileError}</Alert>}

          <Card>
            <Card.Header>
              <Badge bg={getStatusBadgeVariant(application.status)}>
                {getReferralApplicationStatusLabel(application.status)}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Основная информация</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th style={{ width: '40%' }}>ID заявки</th>
                        <td>{application.id}</td>
                      </tr>
                      <tr>
                        <th>Пользователь</th>
                        <td>
                          {application.user?.profile?.full_name && (
                            <div>
                              <strong>{application.user.profile.full_name}</strong>
                            </div>
                          )}
                          <div>
                            <strong>{application.user?.username || '—'}</strong>
                          </div>
                          <small className="text-muted">{application.user?.email || '—'}</small>
                        </td>
                      </tr>
                      <tr>
                        <th>Кандидат</th>
                        <td>{application.candidate_name}</td>
                      </tr>
                      <tr>
                        <th>Направление</th>
                        <td>{application.direction?.name || '—'}</td>
                      </tr>
                      <tr>
                        <th>Тип резюме</th>
                        <td>
                          <Badge
                            bg={application.resume_type === ResumeType.FILE ? 'success' : 'info'}
                          >
                            {getResumeTypeLabel(application.resume_type)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Статус</th>
                        <td>
                          <Badge bg={getStatusBadgeVariant(application.status)}>
                            {getReferralApplicationStatusLabel(application.status)}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th>Дата создания</th>
                        <td>{new Date(application.created_at).toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>

                <Col md={6}>
                  <h5>Резюме</h5>
                  {application.resume_type === ResumeType.URL ? (
                    <div>
                      <p className="text-muted">Резюме предоставлено в виде ссылки</p>
                      {application.resume_url ? (
                        <Button variant="primary" onClick={handleOpenResumeUrl} className="mb-3">
                          Открыть резюме в новой вкладке
                        </Button>
                      ) : (
                        <Alert variant="warning">Ссылка на резюме не предоставлена</Alert>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-muted">Резюме предоставлено в виде файла</p>
                      <Button
                        variant="primary"
                        onClick={handleDownloadResume}
                        disabled={downloadingFile}
                        className="mb-3"
                      >
                        {downloadingFile ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Скачивание...
                          </>
                        ) : (
                          'Скачать резюме'
                        )}
                      </Button>
                    </div>
                  )}

                  <hr />

                  <h5>Комментарий</h5>
                  <p className="text-muted">{application.comment || 'Комментарий отсутствует'}</p>

                  <hr />

                  <h5>Обновление статуса</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Установить статус:</Form.Label>
                      <div className="d-grid gap-2">
                        <Button
                          variant={
                            application.status === ReferralApplicationStatus.PENDING
                              ? 'warning'
                              : 'outline-warning'
                          }
                          onClick={() => handleStatusUpdate(ReferralApplicationStatus.PENDING)}
                          disabled={
                            updating || application.status === ReferralApplicationStatus.PENDING
                          }
                          size="sm"
                        >
                          {application.status === ReferralApplicationStatus.PENDING ? '✓ ' : ''}
                          Ожидает обработки
                        </Button>
                        <Button
                          variant={
                            application.status === ReferralApplicationStatus.REVIEWING
                              ? 'info'
                              : 'outline-info'
                          }
                          onClick={() => handleStatusUpdate(ReferralApplicationStatus.REVIEWING)}
                          disabled={
                            updating || application.status === ReferralApplicationStatus.REVIEWING
                          }
                          size="sm"
                        >
                          {application.status === ReferralApplicationStatus.REVIEWING ? '✓ ' : ''}На
                          рассмотрении
                        </Button>
                        <Button
                          variant={
                            application.status === ReferralApplicationStatus.ACCEPTED
                              ? 'success'
                              : 'outline-success'
                          }
                          onClick={() => handleStatusUpdate(ReferralApplicationStatus.ACCEPTED)}
                          disabled={
                            updating || application.status === ReferralApplicationStatus.ACCEPTED
                          }
                          size="sm"
                        >
                          {application.status === ReferralApplicationStatus.ACCEPTED ? '✓ ' : ''}
                          Принято
                        </Button>
                        <Button
                          variant={
                            application.status === ReferralApplicationStatus.REJECTED
                              ? 'danger'
                              : 'outline-danger'
                          }
                          onClick={() => handleStatusUpdate(ReferralApplicationStatus.REJECTED)}
                          disabled={
                            updating || application.status === ReferralApplicationStatus.REJECTED
                          }
                          size="sm"
                        >
                          {application.status === ReferralApplicationStatus.REJECTED ? '✓ ' : ''}
                          Отклонено
                        </Button>
                      </div>
                    </Form.Group>
                  </Form>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default HiringReferralsDetailPage;
